import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CalendarCheck, ShieldCheck, CarFront, Snowflake, Clock, Star, ArrowRight } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const cta = () => navigate(user && user.role ? "/dashboard" : "/register");

  const features = [
    { icon: CalendarCheck, title: "Book in seconds", desc: "Browse instructors, see live weekly availability, and request a lesson that fits your schedule." },
    { icon: ShieldCheck, title: "No double-booking", desc: "The backend guarantees every slot is protected — no clashes, ever." },
    { icon: CarFront, title: "Vetted instructors", desc: "Real profiles, ratings, vehicles and experience so you drive with confidence." },
    { icon: Clock, title: "Manage anytime", desc: "Cancel or reschedule with a tap. Both sides get instant notifications." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1518203441944-e9578e4b1635?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHx3aW50ZXIlMjByb2FkJTIwZHJpdmluZ3xlbnwwfHx8fDE3ODc0NDEwMTN8MA&ixlib=rb-4.1.0&q=85"
            alt="Winter road" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-arctic-secondary/80" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-arctic-accent text-sm mb-6 border border-white/20">
              <Snowflake className="w-4 h-4" /> Winter-ready driving lessons
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
              Learn to drive with <span className="text-arctic-accent">Arctic</span> confidence.
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl">
              A modern booking platform connecting students with expert driving instructors. Pick a slot, request a lesson, and hit the road.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={cta} data-testid="hero-get-started"
                className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90 text-white px-8 h-12 text-base">
                Get started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/login")} data-testid="hero-login"
                className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20 px-8 h-12 text-base">
                I have an account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-xl">
          <h2 className="font-heading font-bold text-3xl text-arctic-secondary tracking-tight">Everything you need to get licensed</h2>
          <p className="mt-3 text-muted-foreground">Built for students and instructors alike.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {features.map((f, i) => (
            <div key={f.title} style={{ animationDelay: `${i * 80}ms` }}
              className="animate-fade-up bg-card border border-border rounded-2xl p-6 shadow-[0_8px_32px_rgba(0,168,232,0.05)] hover:-translate-y-1 transition-transform duration-200">
              <div className="w-11 h-11 rounded-xl bg-arctic-primary/10 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-arctic-primary" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-arctic-secondary">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Split */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="relative rounded-3xl overflow-hidden min-h-[320px] group">
            <img src="https://images.unsplash.com/photo-1578041262130-633307b3bfd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxzdHVkZW50JTIwZHJpdmluZyUyMGNhcnxlbnwwfHx8fDE3ODc0NDEwMTN8MA&ixlib=rb-4.1.0&q=85"
              alt="Student driving" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-arctic-secondary/90 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <div className="text-arctic-accent text-sm font-semibold mb-1">For students</div>
              <h3 className="font-heading font-bold text-2xl text-white">Find your perfect instructor</h3>
              <Button className="mt-4 rounded-full bg-white text-arctic-secondary hover:bg-white/90" onClick={cta} data-testid="student-cta">Browse instructors</Button>
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden min-h-[320px] group">
            <img src="https://images.unsplash.com/photo-1630406144797-821be1f35d75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxkcml2aW50ZXJ8ZW58MHx8fDE3ODc0NDEwMTN8MA&ixlib=rb-4.1.0&q=85"
              alt="Instructor" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-arctic-primary/90 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <div className="text-white/80 text-sm font-semibold mb-1">For instructors</div>
              <h3 className="font-heading font-bold text-2xl text-white">Manage your schedule & requests</h3>
              <Button className="mt-4 rounded-full bg-white text-arctic-primary hover:bg-white/90" onClick={cta} data-testid="instructor-cta">Start teaching</Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><Snowflake className="w-4 h-4 text-arctic-primary" /> Arctic Driving School</div>
          <div className="flex items-center gap-1"><Star className="w-4 h-4 fill-arctic-primary text-arctic-primary" /> 4.9 average rating</div>
        </div>
      </footer>
    </div>
  );
}
